import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAraTheme } from '../contexts/AraThemeContext';
import { getAraAppState, setAraAppState } from '../utils/ara-storage';
import { ArrowLeft, Check, FloppyDisk } from 'phosphor-react';
import { toast } from 'sonner';

const PATHWAYS = [
  { id: 'supporting_someone', label: "I'm supporting someone through a difficult period" },
  { id: 'challenging_relationship', label: "I'm navigating a challenging relationship" },
  { id: 'communicate_compassionately', label: "I want to communicate more compassionately" },
  { id: 'understand_reactions', label: "I'm struggling to understand someone else's reactions" },
  { id: 'own_needs', label: "I need help recognizing and expressing my own needs" },
  { id: 'own_reflection', label: "I'm here for my own reflection and support" },
  { id: 'just_talk', label: "I'd rather just start talking" },
];

/*
 * AraPreferences — About Me / Support Preferences
 * Lets a returning user review and edit the information they originally
 * provided during Ara onboarding. Reads/writes AraSanctuaryDB.app_state
 * only. Does NOT touch passcode, conversations, or reflections.
 */
const AraPreferences = () => {
  const [name, setName] = useState('');
  const [pathway, setPathway] = useState('');
  const [context, setContext] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { theme } = useAraTheme();
  const navigate = useNavigate();
  const logo = theme === 'dark' ? '/ara-logo-dark.png' : '/ara-logo-light.png';

  useEffect(() => {
    (async () => {
      try {
        const [n, p, c] = await Promise.all([
          getAraAppState('ara_name'),
          getAraAppState('ara_pathway'),
          getAraAppState('ara_context'),
        ]);
        setName(n || '');
        setPathway(p || '');
        setContext(c || '');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setIsSaving(true);
    try {
      await Promise.all([
        setAraAppState('ara_name', name.trim()),
        setAraAppState('ara_pathway', pathway),
        setAraAppState('ara_context', context.trim()),
      ]);
      toast.success('Preferences saved');
    } catch {
      toast.error('Could not save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="ara-root min-h-screen" data-ara-theme={theme} data-testid="ara-preferences">
      <header className="ara-header sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/ara/chat')}
            className="inline-flex items-center gap-2 ara-icon-btn p-2 rounded-lg text-sm"
            data-testid="ara-prefs-back-btn"
          >
            <ArrowLeft size={18} /> <span className="hidden sm:inline">Back to Ara</span>
          </button>
          <img src={logo} alt="" className="w-8 h-8 rounded-full ml-1" />
          <h1 className="font-serif font-semibold ara-text" data-testid="ara-preferences-title">About Me</h1>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="text-center py-16 ara-text-muted">Loading…</div>
        ) : (
          <div className="ara-card rounded-3xl p-6 space-y-6">
            <p className="text-sm ara-text-muted">
              Ara uses this information in the background to meet you more
              gently. Everything stays private on this device, and you can edit
              it anytime.
            </p>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium ara-text mb-2">What may Ara call you?</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="A name or nickname"
                className="ara-input w-full rounded-xl px-4 py-3"
                data-testid="ara-prefs-name-input"
                maxLength={60}
              />
            </div>

            {/* Pathway */}
            <div>
              <label className="block text-sm font-medium ara-text mb-2">What brings you to Ara?</label>
              <div className="space-y-2">
                {PATHWAYS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPathway(p.id === pathway ? '' : p.id)}
                    className={`w-full text-left rounded-xl px-4 py-3 border transition-all flex items-start gap-3 ${
                      pathway === p.id ? 'ara-option-selected' : 'ara-option'
                    }`}
                    data-testid={`ara-prefs-pathway-${p.id}`}
                  >
                    <span className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full ara-option-dot">
                      {pathway === p.id && <Check size={12} weight="bold" />}
                    </span>
                    <span className="text-sm">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Context */}
            <div>
              <label className="block text-sm font-medium ara-text mb-2">Anything you'd like Ara to hold in mind?</label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="A sentence or two — only what you want to share."
                className="ara-input w-full rounded-xl px-4 py-3 min-h-[120px] resize-none"
                data-testid="ara-prefs-context-input"
                maxLength={500}
              />
              <p className="text-xs ara-text-muted mt-2">
                Ara won't retain details about anyone else beyond what you choose to say.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => navigate('/ara/chat')}
                className="ara-cta-ghost rounded-full px-5 py-2 text-sm"
                data-testid="ara-prefs-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={isSaving}
                className="ara-cta-primary inline-flex items-center gap-2 rounded-full px-5 py-2 font-medium disabled:opacity-50"
                data-testid="ara-prefs-save-btn"
              >
                <FloppyDisk size={16} weight="duotone" />
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AraPreferences;
